import {createClient} from '@supabase/supabase-js'

const supabaseUrl = "https://cnpozvvusjsxkydugtlg.supabase.co"
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNucG96dnZ1c2pzeGt5ZHVndGxnIiwicm9sZSI6ImFub24iLCJpYXQiOjE2OTkyOTUxMTYsImV4cCI6MjAxNDg3MTExNn0.bRrcrO1pbKmIF7KXDJn03pTSQhx9VxYA4hGFnbFY3UA'

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// ============================================================================
// MODE override — single switch that controls all sandbox-vs-production
// defaults across the frontend. Set REACT_APP_MODE in your build env (or in
// a local .env file) to flip:
//   - 'production' (DEFAULT): deployed backend + frontend URLs, production
//                    Rainforest bundles. Paired with backend pointed at
//                    https://api.rainforestpay.com/v1. This is the default
//                    so any deployed build that forgets to set the env var
//                    fails safe to production (NOT sandbox).
//   - 'sandbox'   : localhost backend + frontend URLs, sandbox.* Rainforest
//                    bundles. Paired with backend RAINFOREST_BASE_URL pointed
//                    at https://api.sandbox.rainforestpay.com/v1. Use a local
//                    .env file (REACT_APP_MODE=sandbox) to flip this on for
//                    local development.
//
// The per-URL ?demo=true flag (used in Dashboard / PublicInvoice) still works
// as a one-off override regardless of MODE — it force-loads sandbox bundles
// for that single page load even in 'production' mode. That preserves the
// existing demo-link workflow.
// ============================================================================
const MODE = process.env.REACT_APP_MODE || 'production'; // 'sandbox' | 'production'
export const IS_SANDBOX_MODE = MODE === 'sandbox';

const DEFAULT_BASE_URL = process.env.REACT_APP_API_URL || 'https://dentalexpressserver.azurewebsites.net';
const PAYMENTS_BASE_URL = process.env.REACT_APP_PAYMENTS_API_URL || (IS_SANDBOX_MODE
  ? 'http://localhost:5005'
  : 'https://paymentsserver-c7cfamagdrfnb9f7.centralus-01.azurewebsites.net');
const FRONTEND_BASE_URL = process.env.REACT_APP_FRONTEND_URL || (IS_SANDBOX_MODE
  ? 'http://localhost:3000'
  : 'https://payments.joinnewton.com');

//const DEFAULT_BASE_URL = "http://localhost:3001";
// Token refresh logic using Supabase
const refreshAccessToken = async () => {
    try {
        // Check if we have a session
        const {data: currentSession} = await supabase.auth.getSession();
        if (!currentSession?.session) {
            console.log("No session found");
            return null;
        }

        // Option 1: Rely on Supabase's refreshSession which automatically determines if refresh is needed
        const {data, error} = await supabase.auth.refreshSession();
        if (error) {
            console.error("Failed to refresh token:", error);
            // Clear localStorage and redirect to login
            handleAuthFailure();
            return null;
        }

        // Update the token in localStorage
        if (data.session) {
            localStorage.setItem('jwtToken', data.session.access_token);
            if (data.session.refresh_token) {
                localStorage.setItem('refreshToken', data.session.refresh_token);
            }
            return data.session;
        }
        return null;
    } catch (error) {
        console.error("Error in refreshAccessToken:", error);
        return null;
    }
};

// Add a centralized function to handle authentication failures
function handleAuthFailure() {
    // Redirect to login page
    //console.log("auth failure")
    window.location.href = '/login';

    // Optional: You could also dispatch an event for components to listen to
    // window.dispatchEvent(new CustomEvent('auth:logout', {
    //     detail: {reason: 'Session expired or invalid'}
    // }));
}

export async function fetchFromAPI(endpoint, options = {}, baseUrl = DEFAULT_BASE_URL) {
    return doFetchWithRetry(endpoint, options, false, baseUrl);
}

// Convenience function for Payments API calls
export async function fetchFromPaymentsAPI(endpoint, options = {}) {
    return doFetchWithRetry(endpoint, options, false, PAYMENTS_BASE_URL);
}

// Convenience function for Public API calls (no authentication)
export async function fetchFromPublicAPI(endpoint, options = {}) {
    return doFetchWithRetry(endpoint, { ...options, skipAuth: true }, false, PAYMENTS_BASE_URL);
}

// Export base URLs for direct use if needed
export { DEFAULT_BASE_URL, PAYMENTS_BASE_URL, FRONTEND_BASE_URL };

/**
 * Parse an ISO-8601 timestamp from the backend into a Date.
 *
 * Why this exists:
 * Newer writes from the payments backend use `now_iso()` which always
 * includes a timezone designator ("...+00:00" or "...Z"), so they parse
 * unambiguously. However, rows persisted before that change were written
 * with `datetime.utcnow().isoformat()` — a NAIVE string with no timezone.
 * Per the ECMAScript spec, `new Date("2026-04-22T23:20:36")` parses such
 * strings as LOCAL time, which makes a UTC value display shifted by the
 * user's offset.
 *
 * This helper detects the missing designator and appends 'Z' so the
 * value is always interpreted as UTC. Returns `null` if the input is
 * falsy or unparseable.
 */
export function parseBackendDate(value) {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

    // Calendar-day strings (``YYYY-MM-DD``, no time component) represent
    // a *local* calendar date — the backend uses them for fields like
    // ``dueDate``, ``startDate``, ``nextChargeDate`` where no time-of-day
    // exists. ``new Date('2026-05-07')`` interprets the string as UTC
    // midnight, which shifts to the previous calendar day for any user
    // west of UTC (e.g. PST renders 2026-05-07 as "May 6"). Parse as
    // local-midnight instead so the displayed day matches the picked
    // day everywhere.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dateOnly) {
        const [, y, m, d] = dateOnly;
        const out = new Date(Number(y), Number(m) - 1, Number(d));
        return Number.isNaN(out.getTime()) ? null : out;
    }

    // Already has a timezone designator: trailing 'Z' or "+HH:MM" / "-HH:MM" / "+HHMM" / "-HHMM"
    const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(value);
    const safe = hasTz ? value : `${value}Z`;
    const d = new Date(safe);
    return Number.isNaN(d.getTime()) ? null : d;
}

// Helper function that handles the retrying logic
async function doFetchWithRetry(endpoint, options = {}, isRetry = false, baseUrl = DEFAULT_BASE_URL) {
    // pull our custom flag off options so it doesn't reach fetch()
    const { skipAuth = false, ...fetchOptions } = options || {};
  
    // ensure headers object exists (work on a copy)
    const headers = { ...(fetchOptions.headers || {}) };
  
    // Attach/require tokens only when NOT skipping auth
    if (!skipAuth) {
      const token = localStorage.getItem('jwtToken');
  
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else {
        console.error("Token not found in localStorage - user may not be logged in");
  
        // Try to get a fresh session first before failing
        const session = await supabase.auth.getSession();
        if (session.data?.session?.access_token) {
          localStorage.setItem('jwtToken', session.data.session.access_token);
          headers.Authorization = `Bearer ${session.data.session.access_token}`;
        } else {
          console.error("No active session found - redirecting to login endpoint: ", endpoint);
          handleAuthFailure();
          return null;
        }
      }
    }
  
    const finalOptions = { ...fetchOptions, headers };
  
    try {
      const response = await fetch(`${baseUrl}${endpoint}`, finalOptions);
  
      // Handle normal responses
      if (response.ok) {
        return response.json();
      }
  
      // Only try refresh/redirect on 401 when auth is required
      if (!skipAuth && response.status === 401 && !isRetry) {
        console.log("Token expired, attempting refresh...");
  
        const newSession = await refreshAccessToken();
  
        if (newSession) {
          console.log("Token refreshed successfully, retrying request");
          // retry with the same fetch options; auth will be reattached inside
          return doFetchWithRetry(endpoint, { ...fetchOptions, skipAuth }, true, baseUrl);
        } else {
          console.log("Token refresh failed");
          handleAuthFailure();
          throw new Error('Authentication failed - please log in again');
        }
      }
  
      // Handle other errors (only redirect on 403 when auth was required)
      if (!skipAuth && response.status === 403) {
        console.error("Access forbidden");
        handleAuthFailure();
      }
  
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }
  

